import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Search, X, Grid3x3, List } from 'lucide-react';

interface RecipeFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  cuisineFilter: string;
  onCuisineChange: (cuisine: string) => void;
  difficultyFilter: string;
  onDifficultyChange: (difficulty: string) => void;
  viewMode: 'table' | 'grid';
  onViewModeChange: (mode: 'table' | 'grid') => void;
  availableCuisines: string[];
  onClearFilters: () => void;
}

/**
 * Recipe Filters Component
 * 
 * Provides search and filtering controls for recipe manager:
 * - Search by title, cuisine, tags
 * - Filter by cuisine
 * - Filter by difficulty
 * - Toggle view mode (table/grid)
 */
export function RecipeFilters({
  searchQuery,
  onSearchChange,
  cuisineFilter,
  onCuisineChange,
  difficultyFilter,
  onDifficultyChange,
  viewMode,
  onViewModeChange,
  availableCuisines,
  onClearFilters,
}: RecipeFiltersProps) {
  const hasActiveFilters = searchQuery || cuisineFilter || difficultyFilter;

  const difficulties = ['Easy', 'Medium', 'Hard'];

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search recipes by title, cuisine, or tags..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 pr-10"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-martinique"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Cuisine Filter */}
        <Select value={cuisineFilter || 'all'} onValueChange={(val) => onCuisineChange(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Cuisines" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cuisines</SelectItem>
            {availableCuisines.map((cuisine) => (
              <SelectItem key={cuisine} value={cuisine}>
                {cuisine}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Difficulty Filter */}
        <Select value={difficultyFilter || 'all'} onValueChange={(val) => onDifficultyChange(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Levels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            {difficulties.map((difficulty) => (
              <SelectItem key={difficulty} value={difficulty.toLowerCase()}>
                {difficulty}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-muted-foreground hover:text-martinique"
          >
            <X className="mr-2 h-4 w-4" />
            Clear Filters
          </Button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* View Mode Toggle */}
        <div className="flex gap-1 border rounded-lg p-1">
          <Button
            variant={viewMode === 'table' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('table')}
            className="h-8 w-8 p-0"
            title="Table View"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('grid')}
            className="h-8 w-8 p-0"
            title="Grid View"
          >
            <Grid3x3 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Active filters:</span>
          {searchQuery && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-coral/10 text-coral rounded">
              Search: "{searchQuery}"
            </span>
          )}
          {cuisineFilter && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage/10 text-sage rounded">
              Cuisine: {cuisineFilter}
            </span>
          )}
          {difficultyFilter && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded">
              Difficulty: {difficultyFilter}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
